// Mengen-Tabelle je Artikel (Plans/2026-10-17-bestellung-flow-analyse.md §2 Befund 6, §6):
// bestellt -> Packliste -> WE erfasst -> an DR gemeldet, je Kopf ueber alle Container der
// Bestellung, dazu Misch-EK je Container. Die Rechnung kommt fertig vom Server (Akte
// `mengen_tabelle`); hier nur Anzeige. Mengen sind kein Geld: keine Betraege.
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import type { MengenAuffaelligCode, MengenContainer, MengenZeile, OrderMengenTabelle } from "@/api/purchase";
import { Panel } from "@/components/Panel";
import { mengeMitEinheit } from "./einheit";
import type { ContainerKennung } from "./Restmengen";

type Stufe = "packliste" | "we" | "dr";

/** Welche Stufe ein Code als zu klein markiert. */
const STUFE_JE_CODE: Record<MengenAuffaelligCode, Stufe> = {
  packliste_unter_bestellt: "packliste",
  we_unter_packliste: "we",
  dr_unter_we: "dr",
};

const GRUND: Record<MengenAuffaelligCode, string> = {
  packliste_unter_bestellt: "Packliste < bestellt, Rest offen",
  we_unter_packliste: "WE erfasst < Packliste",
  dr_unter_we: "an DR gemeldet < WE erfasst",
};

function kurz(kennung: Map<number, ContainerKennung>, id: number): string {
  return kennung.get(id)?.titel.split(" · ")[0] ?? `#${id}`;
}

function grundText(a: MengenZeile["auffaellig"][number], kennung: Map<number, ContainerKennung>): string {
  if (a.shipment_ids.length === 0) return GRUND[a.code];
  return `${GRUND[a.code]}: ${a.shipment_ids.map((id) => kennung.get(id)?.titel ?? `#${id}`).join(", ")}`;
}

/** Zelle einer Stufe: Summe, darunter die Container, die diese Stufe erreicht haben. */
function StufenZelle({ testId, summe, zeile, stufe, kennung }: {
  testId: string; summe: number; zeile: MengenZeile; stufe: Stufe | null; kennung: Map<number, ContainerKennung>;
}) {
  const luecke = zeile.auffaellig.find((a) => stufe !== null && STUFE_JE_CODE[a.code] === stufe);
  const teile: Array<{ c: MengenContainer; menge: number }> = stufe === null ? [] : zeile.je_container.flatMap((c) => {
    if (stufe === "packliste") return c.packliste > 0 ? [{ c, menge: c.packliste }] : [];
    if (stufe === "we") return c.hat_we || c.we_erfasst > 0 ? [{ c, menge: c.we_erfasst }] : [];
    return c.final_push ? [{ c, menge: c.dr_gemeldet }] : [];
  });
  return (
    <td className="py-2 pr-3 text-right align-top" data-testid={testId}>
      <div className={`font-mono ${luecke ? "font-semibold text-coral" : ""}`}>{mengeMitEinheit(summe, zeile.einheit)}</div>
      {teile.length > 0 ? (
        <div className="mt-0.5 text-[11px] leading-snug text-ink-2">
          {teile.map(({ c, menge }, i) => (
            <span key={c.shipment_id} title={kennung.get(c.shipment_id)?.titel}>
              {i > 0 ? " · " : ""}
              <span className={luecke?.shipment_ids.includes(c.shipment_id) ? "font-semibold text-coral" : ""}>
                {kurz(kennung, c.shipment_id)}: {mengeMitEinheit(menge, zeile.einheit)}
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </td>
  );
}

function MischEkZelle({ zeile, kennung }: { zeile: MengenZeile; kennung: Map<number, ContainerKennung> }) {
  return (
    <td className="py-2 pr-3 align-top text-xs" data-testid="misch-ek">
      {zeile.je_container.length === 0 ? <span className="text-ink-2">—</span> : zeile.je_container.map((c, i) => (
        <span key={c.shipment_id} title={kennung.get(c.shipment_id)?.titel}>
          {i > 0 ? " · " : ""}
          {kurz(kennung, c.shipment_id)}:{" "}
          <span className={c.ek_applied ? "font-semibold text-success" : "text-ink-2"}>{c.ek_applied ? "ja" : "nein"}</span>
        </span>
      ))}
    </td>
  );
}

export function MengenTabelle({ tabelle, kennung, className }: {
  tabelle: OrderMengenTabelle | undefined; kennung: Map<number, ContainerKennung>; className?: string;
}) {
  if (!tabelle || tabelle.zeilen.length === 0) return null;
  const n = tabelle.auffaellige_zeilen;
  return (
    <Panel
      className={className}
      title="Mengen je Artikel"
      subtitle="bestellt → Packliste → WE erfasst → an DR gemeldet, über alle Container. Auffällig, wenn eine Stufe kleiner ist als die vorige."
      actions={
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${n > 0
          ? "border-coral/40 bg-coral-soft text-coral" : "border-success/40 bg-success-soft text-success"}`}>
          {n > 0 ? `${n} Artikel auffällig` : "alle Stufen stimmig"}
        </span>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label="Mengen je Artikel">
          <thead>
            <tr className="border-b border-line text-left text-xs text-ink-2">
              <th className="py-2 pr-3">Artikel</th>
              <th className="py-2 pr-3 text-right">bestellt</th>
              <th className="py-2 pr-3 text-right">Packliste</th>
              <th className="py-2 pr-3 text-right">WE erfasst</th>
              <th className="py-2 pr-3 text-right" title="Zugang in DreamRobot laut Final-Push (inkl. Startmenge); ohne Final-Push 0">
                an DR gemeldet
              </th>
              <th className="py-2 pr-3">Misch-EK</th>
            </tr>
          </thead>
          <tbody>
            {tabelle.zeilen.map((z) => {
              const auffaellig = z.auffaellig.length > 0;
              return (
                <tr
                  key={z.kopf}
                  data-auffaellig={auffaellig ? "true" : "false"}
                  className={`border-b border-line/50 align-top ${auffaellig ? "bg-coral-soft/60 shadow-[inset_3px_0_0_var(--coral)]" : ""}`}
                >
                  <td className="py-2 pl-2 pr-3">
                    <div className="font-medium"><GrundartikelLink code={z.merchant_sku ?? z.sku} /></div>
                    <div className="text-xs text-ink-2">
                      {z.name ?? "—"}
                      {z.order_item_ids.length > 1 ? <> · <span>{z.order_item_ids.length} Positionen</span></> : null}
                    </div>
                    {z.auffaellig.map((a) => (
                      <div key={a.code} className="mt-1 text-[11px] font-semibold text-coral">{grundText(a, kennung)}</div>
                    ))}
                  </td>
                  <StufenZelle testId="menge-bestellt" summe={z.bestellt} zeile={z} stufe={null} kennung={kennung} />
                  <StufenZelle testId="menge-packliste" summe={z.packliste} zeile={z} stufe="packliste" kennung={kennung} />
                  <StufenZelle testId="menge-we" summe={z.we_erfasst} zeile={z} stufe="we" kennung={kennung} />
                  <StufenZelle testId="menge-dr" summe={z.dr_gemeldet} zeile={z} stufe="dr" kennung={kennung} />
                  <MischEkZelle zeile={z} kennung={kennung} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
